import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, KeyRound, RefreshCw, ShieldCheck, Trash2, TriangleAlert } from 'lucide-react'
import { api } from '../api'
import type { Role, Workspace } from '../types'
import { atLeast } from '../types'
import {
  Button, Card, CardTitle, ConfirmButton, Field, Input, Badge,
  ErrorText, Skeleton,
} from '../components/ui'
import { Select, Tabs } from '../components/select'
import { ago } from '../lib/utils'
import { BrandContext } from './BrandContext'
import { TeamSettings, type TeamMember as Member } from './TeamSettings'
import { AccountSecurity } from './AccountSecurity'
import { AuditLog } from './AuditLog'

type CredentialRow = {
  provider: 'kie' | 'elevenlabs'
  label: string
  configured: boolean
  last_checked_at?: string
  verification_failed: boolean
  last_four?: string
  verified_at?: string
  set_by_email?: string
  updated_at?: string
}

type VideoModelPreset = {
  id: string
  name: string
  price: string
  cost_10s_usd: number
  resolution: string
}

// Where to find each key. Shown only inside the form, where someone is
// actually looking for it.
const WHERE: Record<string, string> = {
  kie: 'kie.ai → API keys',
  elevenlabs: 'elevenlabs.io → Profile → API key',
}

export function WorkspaceSettings({ ws, role, initialTab = 'workspace', reviewBrand = false }: {
  ws: string
  role?: Role
  initialTab?: string
  reviewBrand?: boolean
}) {
  const canAdmin = atLeast(role, 'admin')
  // Three unrelated concerns -- what the workspace is called, what it spends
  // money through, and who can reach it -- so they are tabs rather than a
  // stack. Nobody comes here to change the name AND revoke access.
  const [tab, setTab] = useState(initialTab)
  useEffect(() => { setTab(initialTab) }, [initialTab])

  const members = useQuery({
    queryKey: ['members', ws],
    queryFn: () => api.get<Member[]>(`/api/workspaces/${ws}/members`),
  })

  return (
    <div className="space-y-5">
      <Tabs
        value={tab} onValueChange={setTab}
        tabs={[
          { value: 'workspace', label: 'Workspace' },
          { value: 'brand', label: 'Brand context' },
          { value: 'keys', label: 'API keys' },
          { value: 'members', label: 'Team', count: members.data?.length },
          { value: 'account', label: 'Account' },
          ...(canAdmin ? [{ value: 'audit', label: 'Audit log' }] : []),
        ]}
      />
      {tab === 'workspace' && <WorkspaceName ws={ws} canAdmin={canAdmin} />}
      {tab === 'brand' && <BrandContext ws={ws} role={role} startEditing={reviewBrand} />}
      {tab === 'keys' && <Credentials ws={ws} canAdmin={canAdmin} />}
      {tab === 'members' && <TeamSettings ws={ws} role={role} />}
      {tab === 'account' && <AccountSecurity />}
      {tab === 'audit' && canAdmin && <AuditLog key={ws} ws={ws} />}
    </div>
  )
}

function WorkspaceName({ ws, canAdmin }: { ws: string; canAdmin: boolean }) {
  const qc = useQueryClient()
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<{ workspaces: Workspace[] }>('/api/auth/me'),
  })
  const current = me.data?.workspaces.find(w => w.id === ws)
  const [name, setName] = useState('')
	const [videoModel, setVideoModel] = useState('')
	const models = useQuery({
	  queryKey: ['video-models', ws],
	  queryFn: () => api.get<VideoModelPreset[]>(`/api/workspaces/${ws}/video-models`),
	})

	useEffect(() => {
	  if (current) { setName(current.name); setVideoModel(current.video_model || 'gemini-omni') }
	}, [current])

  const save = useMutation({
	mutationFn: () => api.patch<Workspace>(`/api/workspaces/${ws}`, {
	  name: name.trim(), video_model: videoModel,
	}),
    // The name is in the sidebar picker, so both have to refresh.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  })

  if (!current) return <Skeleton className="h-32 w-full" />

	const dirty = name.trim() !== '' && (
	  name.trim() !== current.name || videoModel !== current.video_model
	)

  return (
    <Card className="space-y-4">
      <div>
        <CardTitle>Workspace</CardTitle>
        <p className="mt-1 text-sm text-ink-faint">
          One workspace holds one brand, its avatars, and everything made for it.
        </p>
      </div>

      {/* The slug is not shown: it cannot be changed and nothing in the
          interface asks for it, so displaying it is a field that only invites
          the question of why it is disabled. */}
      <Field label="Name">
        <Input
          value={name} onChange={e => setName(e.target.value)}
          disabled={!canAdmin} maxLength={80} className="max-w-sm"
        />
      </Field>

	  <div className="border-t border-line pt-4">
		<Field label="Primary video model">
		  <Select
			value={videoModel}
			onValueChange={setVideoModel}
			disabled={!canAdmin || models.isLoading}
			ariaLabel="Primary video model"
			className="max-w-md"
			items={(models.data ?? []).map(m => ({
			  value: m.id, label: m.name, hint: m.price,
			}))}
		  />
		</Field>
	  </div>

      <ErrorText error={save.error} />
      {canAdmin && (
        <div className="flex items-center gap-3">
          <Button variant="primary" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {save.isSuccess && !dirty && (
            <span className="flex items-center gap-1.5 text-xs text-good">
              <Check className="size-3.5" /> Saved
            </span>
          )}
        </div>
      )}
    </Card>
  )
}

type BalanceRow = {
  provider: 'kie' | 'elevenlabs'
  label: string
  source?: string
  error?: string
  credits?: number
  usd?: number
  characters_used?: number
  character_limit?: number
  tier?: string
  unavailable?: string
}

function Credentials({ ws, canAdmin }: { ws: string; canAdmin: boolean }) {
  const creds = useQuery({
    queryKey: ['credentials', ws],
    queryFn: () => api.get<CredentialRow[]>(`/api/workspaces/${ws}/credentials`),
  })

  return (
    <Card className="space-y-4">
      <div>
        <CardTitle>API keys</CardTitle>
        <p className="mt-1 text-sm text-ink-faint">
          This workspace's own keys. Everything it generates is billed to them.
        </p>
      </div>

      <ErrorText error={creds.error} />
      {creds.isLoading && <Skeleton className="h-24 w-full" />}

      <div className="space-y-3">
        {creds.data?.map(c => (
          <CredentialRowView key={c.provider} ws={ws} row={c} canAdmin={canAdmin} />
        ))}
      </div>
    </Card>
  )
}

/** Fetches balances only when asked.
 *
 *  Both providers come back in one call, so whichever row asks first answers
 *  for both -- but a row only reveals its own once its own button has been
 *  pressed, so nothing appears that was not requested. Checking costs a round
 *  trip to two external services, which is not something to spend on every
 *  page load when the number is only wanted occasionally.
 */
function useBalance(ws: string, provider: string) {
  const [requested, setRequested] = useState(false)

  const q = useQuery({
    queryKey: ['balance', ws],
    queryFn: () => api.get<BalanceRow[]>(`/api/workspaces/${ws}/credentials/balance`),
    enabled: false,
    // Always re-read on request: a balance is the one number where showing a
    // cached value would defeat the point of asking for it.
    gcTime: 0,
    staleTime: 0,
  })

  return {
    row: requested ? q.data?.find(b => b.provider === provider) : undefined,
    loading: requested && q.isFetching,
    error: requested ? q.error : null,
    requested,
    check: () => { setRequested(true); q.refetch() },
  }
}

/** The balance line for a provider. Each reports what it can: kie gives a
 *  spendable figure, ElevenLabs a character quota, and a key without the
 *  permission to read its own quota says so rather than looking broken. */
function BalanceLine({ row, loading, error }: {
  row?: BalanceRow
  loading: boolean
  error?: unknown
}) {
  if (loading) return <Skeleton className="mt-2 h-6 w-40" />
  // A failed request is distinct from a provider that answered "cannot say":
  // the first is our problem, the second is the key's permissions.
  if (error) {
    return (
      <p className="mt-2 text-xs text-bad">
        Could not reach the provider: {error instanceof Error ? error.message : String(error)}
      </p>
    )
  }
  if (!row) return null

  if (row.error) {
    return <p className="mt-2 text-xs text-bad">Balance unavailable: {row.error}</p>
  }
  if (row.unavailable) {
    return <p className="mt-2 text-xs text-ink-faint">{row.unavailable}</p>
  }

  if (row.usd != null && row.credits != null) {
    const low = row.usd < 5
    return (
      <p className="mt-2 flex items-baseline gap-2">
        <span className={`text-lg font-semibold tabular-nums ${low ? 'text-warn' : 'text-ink'}`}>
          ${row.usd.toFixed(2)}
        </span>
        <span className="text-xs text-ink-faint">
          {Math.round(row.credits).toLocaleString()} credits
          {low ? ' · about three scenes left' : ''}
        </span>
      </p>
    )
  }

  if (row.character_limit != null && row.characters_used != null) {
    const left = row.character_limit - row.characters_used
    const pct = row.character_limit > 0 ? row.characters_used / row.character_limit : 0
    return (
      <div className="mt-2 space-y-1">
        <p className="flex items-baseline gap-2">
          <span className="text-lg font-semibold tabular-nums">{left.toLocaleString()}</span>
          <span className="text-xs text-ink-faint">
            characters left of {row.character_limit.toLocaleString()}
            {row.tier ? ` · ${row.tier}` : ''}
          </span>
        </p>
        <span className="block h-1 w-full max-w-xs overflow-hidden rounded-full bg-surface-3">
          <span
            className={`block h-full rounded-full ${pct > 0.9 ? 'bg-warn' : 'bg-good'}`}
            style={{ width: `${Math.min(100, pct * 100)}%` }}
          />
        </span>
      </div>
    )
  }
  return null
}

function CredentialRowView({ ws, row, canAdmin }: {
  ws: string
  row: CredentialRow
  canAdmin: boolean
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [key, setKey] = useState('')
  const balance = useBalance(ws, row.provider)

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/workspaces/${ws}/credentials/${row.provider}`, { key: key.trim() }),
    onSuccess: () => {
      setKey(''); setEditing(false)
      qc.invalidateQueries({ queryKey: ['credentials', ws] })
    },
  })
  const verify = useMutation({ mutationFn: () => api.post(`/api/workspaces/${ws}/credentials/${row.provider}/verify`), onSettled: () => qc.invalidateQueries({ queryKey: ['credentials', ws] }) })
  const remove = useMutation({
    mutationFn: () => api.del(`/api/workspaces/${ws}/credentials/${row.provider}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credentials', ws] }),
  })


  return (
    <div className="rounded-[--radius-control] border border-line bg-surface-2/40 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <KeyRound className="size-4 text-ink-faint" />
            <span className="font-medium">{row.label}</span>
            {row.configured && row.verified_at && (
              <Badge tone="good"><ShieldCheck className="size-3" /> verified</Badge>
            )}
            {!row.configured && <Badge tone="bad">not set</Badge>}
            {row.verification_failed && <Badge tone="bad">verification failed</Badge>}
          </div>
          {row.last_checked_at && <p className="text-xs text-ink-faint">Checked {ago(row.last_checked_at)}</p>}
          {row.configured && canAdmin && (
            <p className="mt-2 font-mono text-sm">
              <span className="text-ink-faint">••••••••••••</span>
              <span className="text-ink">{row.last_four}</span>
            </p>
          )}
          {row.configured && row.updated_at && (
            <p className="mt-1 text-[11px] text-ink-faint">
              Set {ago(row.updated_at)}{row.set_by_email ? ` by ${row.set_by_email}` : ''}
            </p>
          )}

          {balance.requested && (
            <BalanceLine row={balance.row} loading={balance.loading} error={balance.error} />
          )}
        </div>

        {verify.error && <ErrorText error={verify.error} />}
        {verify.isSuccess && <span className="text-sm">Connection verified</span>}
        {!editing && (
          <div className="flex gap-2">
            {canAdmin && row.configured && <Button size="sm" disabled={verify.isPending} onClick={() => verify.mutate()}>Test connection</Button>}
            {row.configured && canAdmin && (
              // Any member may check what is left before starting work, even
              // though only an admin can change the key.
              <Button
                variant="secondary" size="sm"
                onClick={balance.check} disabled={balance.loading}
              >
                <RefreshCw className={balance.loading ? 'animate-spin' : ''} />
                {balance.loading ? 'Checking…' : balance.requested ? 'Refresh' : 'Check balance'}
              </Button>
            )}
            {canAdmin && (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                {row.configured ? 'Replace' : 'Add key'}
              </Button>
            )}
            {row.configured && canAdmin && (
              <ConfirmButton
                size="icon" title="Remove this key"
                confirmLabel="Remove key"
                disabled={remove.isPending}
                onConfirm={() => remove.mutate()}
              >
                <Trash2 />
              </ConfirmButton>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-4 space-y-3 border-t border-line pt-4">
          <Field
            label={`${row.label} API key`}
            help={`Find it at ${WHERE[row.provider]}. It is verified against the provider before being saved, so a typo is caught here rather than mid-job.`}
          >
            <Input
              type="password"
              value={key}
              onChange={e => setKey(e.target.value)}
              placeholder="Paste the key"
              autoComplete="off"
              autoFocus
              spellCheck={false}
              className="font-mono text-xs"
            />
          </Field>

          <ErrorText error={save.error} />

          <div className="flex items-center gap-2">
            <Button
              variant="primary" size="sm"
              onClick={() => save.mutate()}
              disabled={key.trim().length < 16 || save.isPending}
            >
              {save.isPending ? 'Verifying…' : 'Verify and save'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setKey('') }}>
              Cancel
            </Button>
          </div>

          {row.configured && canAdmin && (
            <p className="flex items-start gap-1.5 text-xs text-warn">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              This replaces the existing key. The old one cannot be recovered.
            </p>
          )}
        </div>
      )}

      <ErrorText error={remove.error} />
    </div>
  )
}
